import { Box, ChakraProps, Flex, Grid, Heading, Text, useToast } from "@chakra-ui/react";
import { useCallback } from "react";
import { useAnalyticsContext, Actions, Features } from "@metriport/shared-internal";
import { retryWebhookRequests } from "../../api/webhook-status";
import { capture } from "../../shared/capture";
import { useAppContext } from "../contexts/app";
import { Button } from "../medical/shared-components/button";
import Constants from "../shared/constants";
import { Label } from "../shared/Label";
import { sleep } from "../shared/util";

export default function WebhookRequests(
  props: {
    webhookRequestsProcessing: number;
    webhookRequestsFailed: number;
    onRetryStart: () => void;
    onRetryEnd: () => void;
    onError: (msg: string) => void;
    isSubmitting: boolean;
  } & ChakraProps
) {
  const {
    webhookRequestsProcessing,
    webhookRequestsFailed,
    onRetryStart,
    onRetryEnd,
    onError,
    isSubmitting,
  } = props;
  const { state } = useAppContext();
  const toast = useToast();
  const Analytics = useAnalyticsContext();

  const retry = useCallback(async () => {
    Analytics.emit(Actions.retry, Features.webhookUrl);
    onRetryStart();
    try {
      await retryWebhookRequests();
      toast({
        title: "Retrying...",
        status: "success",
        duration: 2_000,
        isClosable: true,
      });
      await sleep(2_000);
    } catch (err) {
      capture.error(err, { extra: { context: `webhook.retry` } });
      onError(Constants.errorDefaultMessage);
    }
    onRetryEnd();
  }, [onRetryStart, onRetryEnd, onError, state.authToken, toast]);

  return (
    <Box mt={7}>
      <Flex justifyContent="space-between" alignItems="center">
        <Heading size="sm">Requests</Heading>
        <Button
          isLoading={isSubmitting}
          onClick={retry}
          disabled={webhookRequestsFailed < 1}
          size="sm"
        >
          Retry failed requests
        </Button>
      </Flex>
      <Grid templateColumns="1fr 1fr" gap={4} mt={2}>
        <Flex alignItems="center" gap={2}>
          <Label m={0}>Requests processing:</Label>
          <Text>{webhookRequestsProcessing >= 0 ? webhookRequestsProcessing : 0}</Text>
        </Flex>
        <Flex alignItems="center" gap={2}>
          <Label m={0}>Requests failed:</Label>
          <Text>{webhookRequestsFailed >= 0 ? webhookRequestsFailed : 0}</Text>
        </Flex>
      </Grid>
    </Box>
  );
}
